import { hasStaffView, isStaff, type Profile, type StaffView } from '@/shared/auth/types';
import type { IconName } from '@/shared/ui/icons/names';

/**
 * Menu panelu (konto ogłoszeniodawcy i obsługa portalu) — grupy jak w Argusie. `module` to numer modułu z planu
 * (docs/BUDOWA.md w rynek-backend); ekran modułu, którego jeszcze nie ma, pokazuje stronę „w budowie”.
 */
export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  module: number;
  ready?: boolean;
  /** Ekran obsługi portalu — widoczny z rolą, która ma ten widok, albo z widokiem dodanym osobie. */
  view?: StaffView;
  /** Tylko właściciel konta (płatności, faktury). */
  owner?: boolean;
  /** Tylko konto firmowe (zespół). */
  company?: boolean;
}

export interface NavGroup {
  id: 'account' | 'money' | 'staff';
  title: string;
  items: NavItem[];
}

export const PANEL_HOME: NavItem = { to: '/panel', label: 'Start', icon: 'siatka', module: 0, ready: true };

export const PANEL_GROUPS: NavGroup[] = [
  {
    id: 'account',
    title: 'Moje konto',
    items: [
      { to: '/panel/ogloszenia', label: 'Moje ogłoszenia', icon: 'ogloszenie', module: 2, ready: true },
      { to: '/panel/wiadomosci', label: 'Wiadomości', icon: 'wiadomosci', module: 6, ready: true },
      { to: '/panel/zapisane', label: 'Zapisane i alerty', icon: 'zapisz', module: 12, ready: true },
      { to: '/panel/szukam', label: 'Czego szukam', icon: 'szukaj', module: 12, ready: true },
      {
        to: '/panel/import',
        label: 'Import ofert',
        icon: 'pobierz',
        module: 9,
        ready: true,
        company: true,
        owner: true,
      },
      { to: '/panel/statystyki', label: 'Statystyki ofert', icon: 'statystyki', module: 11, ready: true },
      { to: '/panel/opinie', label: 'Opinie o biurze', icon: 'wyroznij', module: 10, ready: true, company: true },
      { to: '/panel/zespol', label: 'Użytkownicy firmy', icon: 'zespol', module: 1, ready: true, company: true },
      {
        to: '/panel/integracja',
        label: 'Integracja API',
        icon: 'ustawienia',
        module: 8,
        ready: true,
        company: true,
        owner: true,
      },
      { to: '/panel/konto', label: 'Ustawienia konta', icon: 'ustawienia', module: 1, ready: true },
      { to: '/panel/powiadomienia', label: 'Powiadomienia', icon: 'alert', module: 14, ready: true },
    ],
  },
  {
    id: 'money',
    title: 'Płatności',
    items: [
      { to: '/panel/pakiety', label: 'Pakiety i abonament', icon: 'wyroznij', module: 4, owner: true, ready: true },
      { to: '/panel/platnosci', label: 'Płatności', icon: 'platnosc', module: 4, owner: true, ready: true },
      { to: '/panel/faktury', label: 'Faktury', icon: 'umowa', module: 5, owner: true, ready: true },
    ],
  },
  {
    id: 'staff',
    title: 'Obsługa portalu',
    items: [
      {
        to: '/panel/moderacja',
        label: 'Moderacja i zgłoszenia',
        icon: 'ochrona',
        module: 3,
        view: 'LISTINGS_MODERATION',
        ready: true,
      },
      {
        to: '/panel/odwolania',
        label: 'Odwołania i raport DSA',
        icon: 'ochrona',
        module: 10,
        view: 'APPEALS',
        ready: true,
      },
      {
        to: '/panel/opinie-moderacja',
        label: 'Opinie — moderacja',
        icon: 'ochrona',
        module: 10,
        view: 'REVIEWS_MODERATION',
        ready: true,
      },
      {
        to: '/panel/zgloszenia-tresci',
        label: 'Zgłoszenia treści',
        icon: 'uwaga',
        module: 10,
        view: 'CONTENT_REPORTS',
        ready: true,
      },
      {
        to: '/panel/przewodniki',
        label: 'Przewodniki po dzielnicach',
        icon: 'mapa',
        module: 13,
        view: 'GUIDES',
        ready: true,
      },
      {
        to: '/panel/glos-mieszkancow',
        label: 'Głos mieszkańców',
        icon: 'ochrona',
        module: 13,
        view: 'RESIDENT_VOICES',
        ready: true,
      },
      {
        to: '/panel/kontrola-dojazdu',
        label: 'Kontrola czasów dojazdu',
        icon: 'autobus',
        module: 15,
        view: 'COMMUTE_CHECK',
        ready: true,
      },
      {
        to: '/panel/zamowienia',
        label: 'Zamówienia i zwroty',
        icon: 'platnosc',
        module: 4,
        view: 'ORDERS',
        ready: true,
      },
      {
        to: '/panel/faktury-sprzedazy',
        label: 'Faktury sprzedaży',
        icon: 'umowa',
        module: 5,
        view: 'SALES_INVOICES',
        ready: true,
      },
      { to: '/panel/ksiegowosc', label: 'Księgowość', icon: 'kredyt', module: 7, view: 'LEDGER', ready: true },
      {
        to: '/panel/analityka',
        label: 'Statystyki portalu',
        icon: 'statystyki',
        module: 11,
        view: 'ANALYTICS',
        ready: true,
      },
      {
        to: '/panel/uzytkownicy',
        label: 'Konta i użytkownicy',
        icon: 'zespol',
        module: 1,
        view: 'USERS',
        ready: true,
      },
      { to: '/panel/skrzynka', label: 'Skrzynka e-maili', icon: 'email', module: 1, view: 'MAILBOX', ready: true },
      { to: '/panel/cennik', label: 'Cennik i promocje', icon: 'cena', module: 4, view: 'PRICING', ready: true },
      { to: '/panel/kody', label: 'Kody rabatowe', icon: 'cena', module: 4, view: 'DISCOUNT_CODES', ready: true },
      {
        to: '/panel/szablony-emaili',
        label: 'Szablony e-maili',
        icon: 'email',
        module: 14,
        view: 'EMAIL_TEMPLATES',
        ready: true,
      },
      {
        to: '/panel/dokumenty',
        label: 'Dokumenty prawne',
        icon: 'umowa',
        module: 13,
        view: 'LEGAL_DOCUMENTS',
        ready: true,
      },
    ],
  },
];

/** Grupy i pozycje, które widzi użytkownik: obsługa tylko z rolą, płatności tylko właściciel konta. */
export function groupsFor(profile: Profile): NavGroup[] {
  return PANEL_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(
      (i) =>
        (i.view === undefined || hasStaffView(profile, i.view)) &&
        (!i.owner || profile.accountRole === 'ACCOUNT_OWNER') &&
        (!i.company || profile.account.kind === 'COMPANY'),
    ),
  })).filter((g) => g.items.length > 0 && (g.id !== 'staff' || isStaff(profile)));
}

export function findPanelItem(pathname: string): NavItem | undefined {
  if (pathname === PANEL_HOME.to) return PANEL_HOME;
  return PANEL_GROUPS.flatMap((g) => g.items).find((i) => pathname === i.to || pathname.startsWith(`${i.to}/`));
}

/** Nawigacja części publicznej (widok 1 z makiet). */
export const PUBLIC_NAV = [
  { to: '/kupno/mieszkania', label: 'Kupno' },
  { to: '/wynajem/mieszkania', label: 'Wynajem' },
  { to: '/nowe-inwestycje', label: 'Nowe inwestycje' },
  { to: '/kupno/domy', label: 'Domy' },
  { to: '/kupno/dzialki', label: 'Działki' },
  { to: '/szukam', label: 'Czego szukasz?' },
] as const;
